import React, { useContext, useEffect, useState, useRef } from "react";
import {
  GetDashBoardTracksResponse,
  GetDashBoardUsersResponse,
  GetDashBoardProjectsResponse,
  GetDashBoardProjectFilesResponse,
  GetDashBoardPublishedTracksResponse,
} from "../../proto/api_pb";
import {
  getDashBoardTracks,
  getDashBoardUsers,
  getDashBoardProjects,
  getDashBoardProjectFiles,
  getDashBoardPublishedTracks,
} from "../../services/dashboardService";
import { RpcError } from "grpc-web";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import {
  AuthContext,
  AuthContextInterface,
  UserDetailsContext,
  UserDetailsContextInterface,
} from "../../contexts";
import { Box, Typography, useMediaQuery, Alert } from "@mui/material";
import styles from "./styles";
import { CustomCircularProgress } from "../../components/ProgressComponents/CustomCircularProgress";
import { MobileDrawer } from "../../components/MobileDrawer/MobileDrawer";
import { MobileScreenBottomNavigationContainer } from "../Tracks/styles";
import { MobileScreenBottomNavigation } from "../../components/TracksPageComponents/MobileScreenBottomNavigation";
import { useNavigate } from "react-router-dom";

const Dashboard: React.FC = () => {
  const [tracksCount, setTracksCount] = useState<number | null>(null);
  const [usersCount, setUsersCount] = useState<number | null>(null);
  const [projectsCount, setProjectsCount] = useState<number | null>(null);
  const [projectFilesCount, setProjectFilesCount] = useState<number | null>(
    null
  );
  const [publishedTracksCount, setPublishedTracksCount] = useState<
    number | null
  >(null);
  const [tracksLoading, setTracksLoading] = useState<boolean>(false);
  const [usersLoading, setUsersLoading] = useState<boolean>(false);
  const [projectsLoading, setProjectsLoading] = useState<boolean>(false);
  const [projectFilesLoading, setProjectFilesLoading] =
    useState<boolean>(false);
  const [publishedTracksLoading, setPublishedTracksLoading] =
    useState<boolean>(false);
  const [error, setError] = useState<RpcError | null>(null);
  const [hasDataLoaded, setHasDataLoaded] = useState<boolean>(false);
  const isFetchingRef = useRef<boolean>(false);
  const { getValidAccessToken } = useGetValidAccessToken();
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const { isEnterpriseAccount, enterprise } =
    useContext<UserDetailsContextInterface>(UserDetailsContext);
  const navigate = useNavigate();

  const fetchTracksCount = async (
    token: string,
    userId: string,
    enterpriseId: string
  ) => {
    try {
      setTracksLoading(true);
      const response = await getDashBoardTracks(token, userId, enterpriseId);
      if (response instanceof GetDashBoardTracksResponse) {
        setTracksCount(response.getTracksCount());
      }
    } catch (err) {
      console.error("Error fetching tracks count:", err);
      setTracksCount(0);
    } finally {
      setTracksLoading(false);
    }
  };

  const fetchUsersCount = async (
    token: string,
    userId: string,
    enterpriseId: string
  ) => {
    try {
      setUsersLoading(true);
      const response = await getDashBoardUsers(token, userId, enterpriseId);
      if (response instanceof GetDashBoardUsersResponse) {
        setUsersCount(response.getUsersList().length);
      }
    } catch (err) {
      console.error("Error fetching users count:", err);
      setUsersCount(0);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchProjectsCount = async (
    token: string,
    userId: string,
    enterpriseId: string
  ) => {
    try {
      setProjectsLoading(true);
      const response = await getDashBoardProjects(token, userId, enterpriseId);
      if (response instanceof GetDashBoardProjectsResponse) {
        setProjectsCount(response.getProjectsList().length);
      }
    } catch (err) {
      console.error("Error fetching projects count:", err);
      setProjectsCount(0);
    } finally {
      setProjectsLoading(false);
    }
  };

  const fetchProjectFilesCount = async (
    token: string,
    userId: string,
    enterpriseId: string
  ) => {
    try {
      setProjectFilesLoading(true);
      const response = await getDashBoardProjectFiles(
        token,
        userId,
        enterpriseId
      );
      if (response instanceof GetDashBoardProjectFilesResponse) {
        setProjectFilesCount(response.getProjectFilesList().length);
      }
    } catch (err) {
      console.error("Error fetching project files count:", err);
      setProjectFilesCount(0);
    } finally {
      setProjectFilesLoading(false);
    }
  };

  const fetchPublishedTracksCount = async (
    token: string,
    userId: string,
    enterpriseId: string
  ) => {
    try {
      setPublishedTracksLoading(true);
      const response = await getDashBoardPublishedTracks(
        token,
        userId,
        enterpriseId
      );
      if (response instanceof GetDashBoardPublishedTracksResponse) {
        setPublishedTracksCount(response.getPublishedTracksList().length);
      }
    } catch (err) {
      console.error("Error fetching published tracks count:", err);
      setPublishedTracksCount(0);
    } finally {
      setPublishedTracksLoading(false);
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      // Prevent multiple simultaneous calls
      if (isFetchingRef.current || hasDataLoaded) {
        return;
      }

      if (!userId || !enterprise?.id) {
        return;
      }

      isFetchingRef.current = true;
      setError(null);

      try {
        const token = await getValidAccessToken();
        if (!token) {
          console.log("Failed to get valid access token");
          return;
        }

        // Wait for all API calls to complete
        await Promise.all([
          fetchTracksCount(token, userId, enterprise.id),
          fetchUsersCount(token, userId, enterprise.id),
          fetchProjectsCount(token, userId, enterprise.id),
          fetchProjectFilesCount(token, userId, enterprise.id),
          fetchPublishedTracksCount(token, userId, enterprise.id),
        ]);

        // Only set to true after ALL calls succeed
        setHasDataLoaded(true);
      } catch (err) {
        console.error("Error getting access token:", err);
        setError(err as RpcError);
      } finally {
        isFetchingRef.current = false;
      }
    };
    fetchAllData();
  }, [userId, enterprise?.id, hasDataLoaded, getValidAccessToken]);

  if (!isEnterpriseAccount) {
    navigate("not-found");
    return <></>;
  }

  if (error) {
    return (
      <Box sx={{ m: 4 }}>
        <Alert severity="error">
          Failed to load dashboard data. Please try again later.
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <MobileDrawer />
      <Box
        sx={{
          marginX: 4,
          height: "99vh",
          overflow: isSmallScreen ? "scroll" : "hidden",
        }}
      >
        <Typography sx={styles.header} variant="h4">
          Dashboard
        </Typography>

        <Box
          sx={
            isSmallScreen
              ? styles.dashboardContainerSm
              : styles.dashboardContainer
          }
        >
          <Box sx={{ textAlign: "center", width: "100%" }}>
            {usersLoading ? (
              <Box sx={styles.loadingBox}>
                <CustomCircularProgress size={40} />
              </Box>
            ) : (
              <Box sx={styles.filterBox}>
                <Box>Total Users</Box>
                <h1>{usersCount ?? 0}</h1>
              </Box>
            )}
          </Box>
          <Box sx={{ textAlign: "center", width: "100%" }}>
            {projectsLoading ? (
              <Box sx={styles.loadingBox}>
                <CustomCircularProgress size={40} />
              </Box>
            ) : (
              <Box sx={styles.filterBox}>
                <Box>Projects</Box>
                <h1>{projectsCount ?? 0}</h1>
              </Box>
            )}
          </Box>
          <Box sx={{ textAlign: "center", width: "100%" }}>
            {tracksLoading ? (
              <Box sx={styles.loadingBox}>
                <CustomCircularProgress size={40} />
              </Box>
            ) : (
              <Box sx={styles.filterBox}>
                <Box>Total Tracks</Box>
                <h1>{tracksCount ?? 0}</h1>
              </Box>
            )}
          </Box>
          <Box sx={{ textAlign: "center", width: "100%" }}>
            {projectFilesLoading ? (
              <Box sx={styles.loadingBox}>
                <CustomCircularProgress size={40} />
              </Box>
            ) : (
              <Box sx={styles.filterBox}>
                <Box>Total Files</Box>
                <h1>{projectFilesCount ?? 0}</h1>
              </Box>
            )}
          </Box>
          <Box sx={{ textAlign: "center", width: "100%" }}>
            {publishedTracksLoading ? (
              <Box sx={styles.loadingBox}>
                <CustomCircularProgress size={40} />
              </Box>
            ) : (
              <Box sx={styles.filterBox}>
                <Box>Published Tracks</Box>
                <h1>{publishedTracksCount ?? 0}</h1>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <MobileScreenBottomNavigationContainer>
        <MobileScreenBottomNavigation />
      </MobileScreenBottomNavigationContainer>
    </>
  );
};

export default Dashboard;
